import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../layouts/Header";
import Toaster from "../../components/ui/toaster";
import DigitalWalletGradient from "../../components/DigitalWalletGradient";
import SideNav from "../../components/SideNav";
import SendReceivePage from "../../components/SendReceivePage";
import { TransactionHistory } from "../../components/transaction-history";
import BuySellPage from "../../components/BuySellPage";
import EightyEightPay from "../../components/EightyEightPay";
import { useWalletBalance } from "../../hooks/query/queries";
import { useAuth } from "../../context/AuthContext";
import {
  useActiveAccount,
  useAvailableAssets,
  useCryptoAssets,
  useFetchAllTradablePairs,
  useNetworksByAssets,
  useFeatureFlagsByCategory,
} from "../../hooks/query/queries";
import { usePairStore } from "../../store/useStore";
import WithdrawParent from "../../components/WithdrawParent";
import BuySellPageSkeleton from "../../components/skeletons/BuySellPageSkeleton";
import { useSendFlowState } from "../../hooks/useSendFlowState";
import WalletCarousel from "../../components/WalletCarousel";
import sendCrypto from "../../assets/images/sentCrypto.png";
import CryptoAssetComponentDashboard from "../../components/CryptoAssetsDashboard";
import MarketDashboard from "../../components/MarketDashboard";
import BuySellCarousel from "../../components/BuySellCarousel";
import LiveTransactions from "../../components/LiveTransactions";
import AddressBookPage from "../../components/AddressBookPage";

interface WalletAsset {
  assetId?: string;
  _id?: string;
  asset?: string;
  assetName?: string;
  assetIcon?: string;
  currencyType?: "Crypto" | "Fiat";
  type?: "Crypto" | "Fiat";
  availableAmount?: number;
  availableAmountFormatted?: string;
  valueInLocalCurrency?: string;
  [key: string]: unknown;
}

interface LocationState {
  tabActive?: string;
  selectedCrypto?: WalletAsset;
  type?: string;
  selectedTab?: string;
  parentTab?: string;
  childTab?: string;
  parentNavTab?: string;
}

interface WalletBalance {
  assetBalance: unknown[];
  assets?: WalletAsset[];
  assetBalances?: {
    fiat?: {
      zar?: {
        availableAmount?: number;
      };
    };
  };
  portfolioValue?: number;
  currency?: string;
  walletBalance?: number;
  [key: string]: unknown;
}

export default function DigitalWallet() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    tabActive,
    selectedCrypto,
    type,
    selectedTab,
    parentTab,
    childTab,
    parentNavTab,
  } = (location.state as LocationState) || {};

  const urlPayId = searchParams.get("payId");
  const urlTab = searchParams.get("tab");

  const { setPairObject } = usePairStore();
  const [, setLoadingAsset] = useState<boolean>(false);
  const { data: tradingAssets } = useCryptoAssets();
  const { data: pairObject } = useFetchAllTradablePairs();
  const { clearState } = useSendFlowState();

  useEffect(() => {
    if (pairObject) {
      setPairObject(pairObject);
    }
  }, [pairObject, setPairObject]);

  const { data: tradingNets } = useNetworksByAssets(
    tradingAssets,
    setLoadingAsset
  );

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [transactionTab, setTransactionTab] = useState<string>("");
  const { data: walletBalanceData } = useWalletBalance();
  const wallet = useMemo(() => {
    if (!walletBalanceData) return null;
    return {
      ...walletBalanceData,
      assetBalances: {
        crypto: (walletBalanceData.assets || []).filter(
          (asset: WalletAsset) =>
            asset.currencyType === "Crypto" || asset.type === "Crypto"
        ),
        fiat: (walletBalanceData.assets || []).filter(
          (asset: WalletAsset) =>
            asset.currencyType === "Fiat" || asset.type === "Fiat"
        ),
      },
    };
  }, [walletBalanceData]) as WalletBalance | null;
  const [asset, setAsset] = useState<unknown>(null);
  const [firstTimeUser, setFirstTimeUser] = useState<boolean>(true);
  const [showBuySellCarousel, setShowBuySellCarousel] = useState<boolean>(true);

  const { user: userProfile } = useAuth();

  const userId = userProfile?._id;
  const accountId = userProfile?.subAccountDetails[0]?._id;
  const { data: activeAccount } = useActiveAccount(userId, accountId);
  const mainAccount = activeAccount?.isMainAccount;
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const zarBalance = useMemo(() => {
    return wallet?.assetBalances?.fiat?.zar?.availableAmount ?? 0;
  }, [wallet]);

  const params = {
    id: userId,
  };

  const { data: assetsOwned, isLoading: isLoadingAssetsOwned } =
    useAvailableAssets({
      isSend: true,
      isEightyeightPay: false,
      investArray: params,
      balanceParams: { ...params },
      balanceParamsEightEight: params,
    });

  useEffect(() => {
    if (
      Array.isArray(assetsOwned) &&
      assetsOwned.length > 0 &&
      assetsOwned.some((asset) => asset.availableAmount > 0)
    ) {
      setFirstTimeUser(false);
      setShowBuySellCarousel(false);
    }
  }, [assetsOwned]);

  useEffect(() => {
    clearState();
  }, [activeTab, clearState]);

  useEffect(() => {
    if (urlPayId && urlTab === "pay") {
      setActiveTab("80eightpay");
      return;
    }

    if (parentNavTab) {
      setActiveTab(parentNavTab);
    } else if (type && tabActive) {
      setTransactionTab(tabActive);
      setActiveTab(type);
      setAsset(selectedCrypto);
    } else if (parentTab) {
      setActiveTab(parentTab);
    }
  }, [
    parentTab,
    type,
    parentNavTab,
    tabActive,
    selectedCrypto,
    urlPayId,
    urlTab,
  ]);

  // Resolve selectedCrypto to matching asset from tradingAssets so Buy/Sell selects correctly
  useEffect(() => {
    if (!selectedCrypto || type !== "buy-sell" || !tradingAssets?.length) {
      return;
    }
    const incoming = selectedCrypto as WalletAsset;
    const symbol =
      incoming.symbol?.toUpperCase() ||
      (incoming.asset && String(incoming.asset).toUpperCase());
    const assetId = incoming.assetId || incoming._id;
    const match =
      tradingAssets.find(
        (a: WalletAsset) =>
          a.assetId === assetId ||
          a._id === assetId ||
          (a.symbol && a.symbol.toUpperCase() === symbol) ||
          (a.asset && String(a.asset).toUpperCase() === symbol)
      ) ?? null;
    if (match) {
      setAsset(match);
    }
  }, [type, selectedCrypto, tradingAssets]);

  const handleTabChange = (tabId: string) => {
    clearState();
    setTransactionTab("");
    setAsset(null);
    setActiveTab(tabId);
    navigate(location.pathname, {
      replace: true,
      state: {
        parentNavTab: tabId,
      },
    });
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />
      <div className="px-3 lg:px-14 py-6 bg-bg-light-gray rounded-t-2xl min-h-[calc(100vh-100px)]">
        <div className="flex items-center flex-wrap lg:flex-nowrap gap-4 w-full mb-4">
          <DigitalWalletGradient />
          <div className="w-full">
            {firstTimeUser ? (
              <WalletCarousel />
            ) : (
              <CryptoAssetComponentDashboard
                styleClasses="w-full lg:h-[441px] h-[371px]"
                fromDashboard={false}
              />
            )}
          </div>
        </div>
        <Toaster />
        <div className="flex flex-wrap lg:flex-nowrap lg:space-x-10 space-x-0 ">
          <aside className="w-[30%] min-w-[302px] p-[24px] hidden lg:block">
            <SideNav
              activeTab={activeTab}
              mainAccount={mainAccount}
              onTabChange={handleTabChange}
            />
          </aside>
          <main className="w-full space-y-4 mb-10">
            {activeTab === "overview" &&
              (!firstTimeUser ? (
                <>
                  <LiveTransactions />
                  <TransactionHistory userId={userProfile._id} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-[437px] rounded-2xl border border-[#EBE9EF] p-6 bg-white gap-2">
                  <img
                    src={sendCrypto}
                    alt="sendCrypto"
                    className="w-[194px] h-[136px]"
                  />
                  <p className="text-[#33125F] text-[24px] font-semibold">
                    Your crypto journey starts now
                  </p>
                  <p className="text-[#423E51] text-[14px] font-normal">
                    We couldn't find any transactions for this account. New
                    activity will appear here.
                  </p>
                  <button
                    onClick={() => {
                      setTransactionTab("Buy");
                      handleTabChange("buy-sell");
                    }}
                    className="border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full h-[48px] w-[150px] mt-4 hover:bg-[#F5F4F7] transition-colors"
                  >
                    Buy Crypto
                  </button>
                </div>
              ))}

            {activeTab === "market" && (
              <MarketDashboard fromDashboard={false} />
            )}

            {activeTab === "buy-sell" &&
              (featureFlags?.buyCrypto || featureFlags?.sellCrypto) &&
              (isLoadingAssetsOwned ? (
                <BuySellPageSkeleton />
              ) : showBuySellCarousel ? (
                <BuySellCarousel
                  onGetStarted={() => setShowBuySellCarousel(false)}
                />
              ) : (
                <BuySellPage
                  assetToSelect={asset}
                  pairObject={pairObject}
                  tab={transactionTab}
                  quickActionTab={selectedTab}
                  childTab={childTab}
                  tradingAssets={tradingAssets}
                  zarBalance={zarBalance}
                />
              ))}

            {activeTab === "send-receive" &&
              (featureFlags?.sendCrypto || featureFlags?.receiveCrypto) && (
                <SendReceivePage
                  assetToSelect={asset}
                  tab={transactionTab}
                  tradingNets={tradingNets}
                  tradingAssets={tradingAssets}
                  childTab={childTab}
                />
              )}

            {activeTab === "withdraw-deposit" &&
              (featureFlags?.deposit || featureFlags?.withdraw) && (
                <WithdrawParent tab={selectedTab} childTab={childTab} />
              )}

            {mainAccount === true && featureFlags?.eightPay && (
              <>
                {activeTab === "80eightpay" && (
                  <EightyEightPay userId={userId} initialPayId={urlPayId} />
                )}
              </>
            )}

            {activeTab === "address-book" && <AddressBookPage />}
          </main>
        </div>
      </div>
    </div>
  );
}
